import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, MapPin, Package, Truck, User, Phone, Clock, DollarSign, FileText, Camera, Loader2, Edit, MessageSquare, Upload, Send, Image } from 'lucide-react';
import { toast } from 'sonner';
import MapView from '@/components/MapView';
import LoadChargesEditor from '@/components/admin/LoadChargesEditor';
import { getLoadStatusClasses, getDeliveryTypeClasses } from '@/lib/statusColors';

const STATUS_LABELS = {
  draft: 'Draft', posted: 'Posted', assigned: 'Assigned', at_pickup: 'At Pickup',
  in_transit: 'In Transit', at_delivery: 'At Delivery', completed: 'Completed',
  invoiced: 'Invoiced', cancelled: 'Cancelled',
};
const TRANSITIONS = {
  draft: ['posted','cancelled'], posted: ['assigned','draft','cancelled'],
  assigned: ['at_pickup','posted'], at_pickup: ['in_transit'],
  in_transit: ['at_delivery'], at_delivery: ['completed'],
  completed: ['invoiced','at_delivery'],
};
const TIMELINE_COLORS = {
  draft: 'bg-zinc-500', posted: 'bg-blue-400', assigned: 'bg-purple-400',
  at_pickup: 'bg-brand-accent', in_transit: 'bg-blue-400', at_delivery: 'bg-cyan-400',
  completed: 'bg-emerald-400', invoiced: 'bg-green-400', cancelled: 'bg-red-400',
};

export default function AdminLoadDetail() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pod, setPod] = useState(null);
  const [charges, setCharges] = useState([]);
  const [files, setFiles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showCharges, setShowCharges] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [notePhotos, setNotePhotos] = useState([]);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [liveDriverLoc, setLiveDriverLoc] = useState(null);
  const noteFileRef = useRef(null);

  // Haversine distance helper
  const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const fetchData = useCallback(async () => {
        try {
      const [loadRes, driversRes] = await Promise.all([
        api.get(`/loads/${loadId}`),
        api.get('/drivers'),
      ]);
      setLoad(loadRes.data);
      setDrivers(driversRes.data || []);
      // Fetch related data
      api.get(`/loads/${loadId}/pod`).then(r => setPod(r.data)).catch(() => setPod(null));
      api.get(`/loads/${loadId}/charges`).then(r => setCharges(r.data || [])).catch(() => {});
      api.get(`/loads/${loadId}/files`).then(r => setFiles(r.data || [])).catch(() => {});
      api.get(`/invoices?load_id=${loadId}`).then(r => { if (r.data?.length) setInvoice(r.data[0]); }).catch(() => {});
    } catch (err) {
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [token, loadId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live driver GPS polling every 10 seconds
  useEffect(() => {
    if (!load?.driver_id) return;
    let active = true;
    const poll = () => {
      api.get(`/tracking/driver/${load.driver_id}`)
        .then(res => { if (active && res.data?.lat) setLiveDriverLoc(res.data); })
        .catch(() => {});
    };
    poll(); // fetch immediately
    const interval = setInterval(poll, 10000);
    return () => { active = false; clearInterval(interval); };
  }, [load?.driver_id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/loads/${loadId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleAssign = async (driverId) => {
    try {
      await api.put(`/loads/${loadId}/assign`, { driver_id: driverId });
      toast.success('Driver assigned');
      fetchData();
    } catch (err) { toast.error('Failed to assign'); }
  };

  const handleCreateInvoice = async () => {
    try {
      const res = await api.post('/invoices', { load_id: loadId });
      setInvoice(res.data);
      toast.success('Invoice created');
    } catch (err) { toast.error('Failed to create invoice'); }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() && notePhotos.length === 0) return;
    setSubmittingNote(true);
    try {
      // Upload photos to Supabase storage
      const photoUrls = [];
      for (const file of notePhotos) {
        const filePath = `notes/${loadId}/${Date.now()}_${file.name}`;
        const { error: uploadErr } = await supabase.storage.from('uploads').upload(filePath, file);
        if (uploadErr) throw uploadErr;
        const { data: urlData } = supabase.storage.from('uploads').getPublicUrl(filePath);
        photoUrls.push(urlData.publicUrl);
      }
      await api.post(`/loads/${loadId}/notes`, {
        content: noteContent.trim(),
        photo_urls: photoUrls,
      });
      setNoteContent('');
      setNotePhotos([]);
      toast.success('Note added');
      fetchData();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-accent" /></div>;
  if (!load) return <div className="text-center py-20 text-muted-foreground">Load not found</div>;

  const addr = (load, prefix) => {
    const street = load[`${prefix}_street`];
    const city = load[`${prefix}_city`];
    if (street || city) return [street, city, load[`${prefix}_province`], load[`${prefix}_postal`]].filter(Boolean).join(', ');
    return load[`${prefix}_address`] || '';
  };

  const chargeTotals = charges.reduce((a, c) => ({
    client: a.client + (parseFloat(c.client_charges) || 0),
    carrier: a.carrier + (parseFloat(c.carrier_charges) || 0),
    tax: a.tax + (parseFloat(c.gst) || 0) + (parseFloat(c.hst) || 0) + (parseFloat(c.pst) || 0),
  }), { client: 0, carrier: 0, tax: 0 });

  const driver = drivers.find(d => d.user_id === load.driver_id);
  const transitions = TRANSITIONS[load.status] || [];

  const driverLoc = liveDriverLoc || driver?.last_location;
  const isPickupPhase = ['assigned', 'at_pickup'].includes(load?.status);
  const etaMin = driverLoc?.lat
    ? Math.round((getDistanceKm(
        driverLoc.lat, driverLoc.lng,
        isPickupPhase ? load.pickup_lat : load.delivery_lat,
        isPickupPhase ? load.pickup_lng : load.delivery_lng
      ) / 80) * 60)
    : null;

  const mapMarkers = [];
  if (load.pickup_lat) mapMarkers.push({ id: 'p', lat: load.pickup_lat, lng: load.pickup_lng, label: load.pickup_name || 'Pickup', type: 'pickup' });
  if (load.delivery_lat) mapMarkers.push({ id: 'd', lat: load.delivery_lat, lng: load.delivery_lng, label: load.delivery_name || 'Delivery', type: 'delivery' });
  if (driverLoc?.lat) mapMarkers.push({ id: 't', lat: driverLoc.lat, lng: driverLoc.lng, label: driver?.name || 'Driver', type: 'truck', color: '#E5A100' });

  const mapLoads = [{
    pickup_lat: load.pickup_lat, pickup_lng: load.pickup_lng,
    delivery_lat: load.delivery_lat, delivery_lng: load.delivery_lng,
    pickup_name: load.pickup_name, delivery_name: load.delivery_name,
    driver_lat: driverLoc?.lat || null,
    driver_lng: driverLoc?.lng || null,
  }];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/loads')} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-foreground font-mono">{load.load_id}</h1>
            {load.reference_number && (
              <Badge variant="outline" className="font-mono text-xs bg-muted text-foreground border-input px-2 py-0.5">
                Ref: {load.reference_number}
              </Badge>
            )}
            <Badge className={`${getLoadStatusClasses(load.status)} text-xs`}>{STATUS_LABELS[load.status]}</Badge>
            {load.delivery_type && <Badge className={`${getDeliveryTypeClasses(load.delivery_type)} text-xs`}>{load.delivery_type}</Badge>}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Rate', value: `$${Number(load.rate || 0).toFixed(2)}`, color: 'text-emerald-400' },
          { label: 'Distance', value: `${Math.round((load.distance_miles || 0) * 1.60934)} km`, color: 'text-blue-400' },
          { label: 'Weight', value: `${(load.weight_lbs || 0).toLocaleString()} lbs`, color: 'text-purple-400' },
          { label: 'Charges', value: `$${chargeTotals.client.toFixed(2)}`, color: 'text-brand-accent' },
        ].map(s => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-3">
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Map at top */}
      {load.pickup_lat && load.delivery_lat && (
        <Card className="bg-card border-border overflow-hidden relative">
          <div className="h-[350px]">
            <MapView markers={mapMarkers} loads={mapLoads} dark={false} zoom={6} showSatelliteToggle showLegend />
          </div>
          {etaMin !== null && ['assigned', 'at_pickup', 'in_transit'].includes(load.status) && (
            <div className="absolute top-3 right-3 bg-card/90 backdrop-blur border border-border rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg z-10">
              <Truck className="w-4 h-4 text-brand-accent" />
              <span className="text-xs font-medium text-foreground">
                Driver is ~{etaMin} min away{isPickupPhase ? ' from pickup' : ' from delivery'}
              </span>
            </div>
          )}
        </Card>
      )}

      {/* 3-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1: Load Details */}
        <div className="space-y-4">
          {/* Pickup */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-400" /> PICKUP</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              <p className="text-foreground font-medium">{load.pickup_name || 'N/A'}</p>
              <p className="text-muted-foreground">{addr(load, 'pickup')}</p>
              {load.shipper_contact_name && <p className="text-muted-foreground"><Phone className="w-3 h-3 inline mr-1" />{load.shipper_contact_name} {load.shipper_contact_phone}</p>}
              {load.pickup_hours && <p className="text-muted-foreground"><Clock className="w-3 h-3 inline mr-1" />{load.pickup_hours}</p>}
              {load.pickup_date && <p className="text-muted-foreground">Date: {load.pickup_date}</p>}
            </CardContent>
          </Card>

          {/* Delivery */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-red-400" /> DELIVERY</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              <p className="text-foreground font-medium">{load.delivery_name || 'N/A'}</p>
              <p className="text-muted-foreground">{addr(load, 'delivery')}</p>
              {load.receiver_contact_name && <p className="text-muted-foreground"><Phone className="w-3 h-3 inline mr-1" />{load.receiver_contact_name} {load.receiver_contact_phone}</p>}
              {load.delivery_hours && <p className="text-muted-foreground"><Clock className="w-3 h-3 inline mr-1" />{load.delivery_hours}</p>}
              {load.delivery_date && <p className="text-muted-foreground">Date: {load.delivery_date}</p>}
            </CardContent>
          </Card>

          {/* Cargo */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><Package className="w-3 h-3 text-brand-accent" /> CARGO</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              <p className="text-foreground">{load.cargo_description}</p>
              <div className="flex gap-4 text-muted-foreground">
                <span>{(load.weight_lbs || 0).toLocaleString()} lbs</span>
                {load.box_count > 0 && <span>{load.box_count} boxes</span>}
                {load.pallet_count > 0 && <span>{load.pallet_count} pallets</span>}
              </div>
              {load.equipment_type_required && <p className="text-muted-foreground"><Truck className="w-3 h-3 inline mr-1" />{load.equipment_type_required}</p>}
              {load.special_instructions && <p className="text-amber-400 italic mt-2">{load.special_instructions}</p>}
            </CardContent>
          </Card>

          {/* Packages */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><Package className="w-3 h-3 text-cyan-400" /> PACKAGES</CardTitle></CardHeader>
            <CardContent className="text-xs">
              {load.packages && load.packages.length > 0 ? (
                <div className="space-y-2">
                  {load.packages.map((pkg, i) => {
                    const typeColors = {
                      box: 'bg-blue-500/20 text-blue-400',
                      pallet: 'bg-purple-500/20 text-purple-400',
                      skid: 'bg-amber-500/20 text-amber-400',
                      crate: 'bg-emerald-500/20 text-emerald-400',
                      drum: 'bg-red-500/20 text-red-400',
                    };
                    const colorClass = typeColors[pkg.type?.toLowerCase()] || 'bg-zinc-500/20 text-zinc-400';
                    return (
                      <div key={pkg.package_id || i} className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                        <Badge className={`${colorClass} text-[10px] px-1.5 py-0 capitalize`}>{pkg.type || 'Other'}</Badge>
                        <span className="text-foreground">Qty: {pkg.quantity || 1}</span>
                        <span className="text-muted-foreground">{pkg.weight_lbs ? `${pkg.weight_lbs} lbs` : ''}</span>
                        {(pkg.length || pkg.width || pkg.height) && (
                          <span className="text-muted-foreground font-mono text-[10px]">
                            {pkg.length || 0}&times;{pkg.width || 0}&times;{pkg.height || 0} in
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No packages added</p>
              )}
            </CardContent>
          </Card>

          {/* Driver */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><User className="w-3 h-3 text-purple-400" /> DRIVER</CardTitle></CardHeader>
            <CardContent>
              {driver ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-purple-400">{driver.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm text-foreground font-medium">{driver.name}</p>
                      <p className="text-xs text-muted-foreground">{driver.phone || driver.email}</p>
                    </div>
                  </div>
                  {etaMin !== null && ['assigned', 'at_pickup', 'in_transit'].includes(load.status) && (
                    <div className="flex items-center gap-2 text-xs bg-brand-accent/10 text-brand-accent rounded-md px-2 py-1.5">
                      <Clock className="w-3 h-3" />
                      <span>~{etaMin} min away{isPickupPhase ? ' from pickup' : ' from delivery'}</span>
                    </div>
                  )}
                </div>
              ) : ['posted', 'draft'].includes(load.status) ? (
                <select onChange={e => e.target.value && handleAssign(e.target.value)}
                  className="w-full bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2">
                  <option value="">Assign a driver...</option>
                  {drivers.map(d => <option key={d.user_id} value={d.user_id}>{d.name}</option>)}
                </select>
              ) : <p className="text-xs text-muted-foreground">No driver assigned</p>}
            </CardContent>
          </Card>

          {/* Status Actions */}
          {transitions.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {transitions.map(t => (
                <Button key={t} size="sm" onClick={() => handleStatusChange(t)}
                  className="bg-muted text-foreground text-xs hover:bg-muted/80">
                  {STATUS_LABELS[t] || t}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Column 2: Audit Trail + Notes */}
        <div className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">AUDIT TRAIL</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-0">
                  {(load.status_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex gap-3 pb-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${TIMELINE_COLORS[h.status] || 'bg-zinc-500'} flex-shrink-0`} />
                        {i < (load.status_history?.length || 0) - 1 && <div className="w-px flex-1 bg-input mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className={`${getLoadStatusClasses(h.status)} text-[9px] px-1.5 py-0`}>{STATUS_LABELS[h.status] || h.status}</Badge>
                          <span className="text-[10px] text-muted-foreground">{h.by_name || h.by || 'System'}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(h.timestamp).toLocaleString()}
                        </p>
                        {h.notes && <p className="text-[10px] text-muted-foreground mt-0.5 italic">{h.notes}</p>}
                      </div>
                    </div>
                  ))}
                  {(!load.status_history || load.status_history.length === 0) && (
                    <p className="text-xs text-muted-foreground text-center py-8">No history</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquare className="w-3 h-3 text-blue-400" /> NOTES</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {/* Add Note Form */}
              <div className="space-y-2 p-3 bg-muted/50 rounded-md border border-input">
                <textarea
                  value={noteContent}
                  onChange={e => setNoteContent(e.target.value)}
                  placeholder="Add a note..."
                  rows={2}
                  className="w-full bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-brand-accent"
                />
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={noteFileRef}
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={e => setNotePhotos(prev => [...prev, ...Array.from(e.target.files)])}
                    />
                    <Button variant="ghost" size="sm" className="text-muted-foreground h-7 text-xs gap-1" onClick={() => noteFileRef.current?.click()}>
                      <Image className="w-3 h-3" /> Photos
                    </Button>
                    {notePhotos.length > 0 && (
                      <span className="text-[10px] text-muted-foreground">{notePhotos.length} file(s) selected</span>
                    )}
                  </div>
                  <Button size="sm" onClick={handleAddNote} disabled={submittingNote || (!noteContent.trim() && notePhotos.length === 0)}
                    className="bg-brand-accent text-black h-7 text-xs gap-1 hover:bg-brand-accent/80">
                    {submittingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Add
                  </Button>
                </div>
                {notePhotos.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {notePhotos.map((f, i) => (
                      <div key={i} className="relative group">
                        <img src={URL.createObjectURL(f)} alt="" className="w-10 h-10 object-cover rounded border border-input" />
                        <button onClick={() => setNotePhotos(prev => prev.filter((_, j) => j !== i))}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes List */}
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {(load.notes_list || []).map((note, i) => (
                    <div key={note.note_id || i} className="p-3 bg-muted/30 rounded-md border border-border">
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-brand-accent">{(note.user_name || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-foreground">{note.user_name || 'Unknown'}</span>
                            {note.user_role && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 capitalize border-input text-muted-foreground">{note.user_role}</Badge>
                            )}
                            <span className="text-[10px] text-muted-foreground">{new Date(note.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-foreground mt-1 whitespace-pre-wrap">{note.content}</p>
                          {note.photo_urls?.length > 0 && (
                            <div className="flex gap-1 mt-2 flex-wrap">
                              {note.photo_urls.map((url, pi) => (
                                <a key={pi} href={url} target="_blank" rel="noreferrer">
                                  <img src={url} alt={`Note photo ${pi + 1}`} className="w-14 h-14 object-cover rounded border border-input hover:opacity-80 transition-opacity cursor-pointer" />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!load.notes_list || load.notes_list.length === 0) && (
                    <p className="text-xs text-muted-foreground text-center py-6">No notes yet</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Column 3: Payment & Delivery */}
        <div className="space-y-4">
          {/* Charges */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3 h-3 text-brand-accent" /> CHARGES</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowCharges(true)} className="text-brand-accent h-6 text-xs gap-1">
                  <Edit className="w-3 h-3" /> Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="text-xs space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Client Total</span><span className="text-foreground font-bold">${chargeTotals.client.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Carrier Total</span><span className="text-foreground">${chargeTotals.carrier.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="text-muted-foreground">${chargeTotals.tax.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-input pt-2">
                <span className="text-muted-foreground font-bold">Grand Total</span>
                <span className="text-brand-accent font-bold">${(chargeTotals.client + chargeTotals.tax).toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">{charges.length} charge line(s)</p>
            </CardContent>
          </Card>

          {/* Invoice */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3 text-blue-400" /> INVOICE</CardTitle></CardHeader>
            <CardContent className="text-xs">
              {invoice ? (
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-muted-foreground">Number</span><span className="text-foreground">{invoice.invoice_number}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge className="text-[9px]">{invoice.status}</Badge></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="text-foreground">${Number(invoice.amount).toFixed(2)}</span></div>
                </div>
              ) : (
                <Button size="sm" onClick={handleCreateInvoice} className="w-full bg-muted text-foreground text-xs">Create Invoice</Button>
              )}
            </CardContent>
          </Card>

          {/* POD */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><Camera className="w-3 h-3 text-emerald-400" /> PROOF OF DELIVERY</CardTitle></CardHeader>
            <CardContent className="text-xs">
              {pod ? (
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-muted-foreground">Receiver</span><span className="text-foreground">{pod.receiver_name}</span></div>
                  {pod.signature_url && (
                    <div><p className="text-muted-foreground mb-1">Signature:</p><img src={pod.signature_url} alt="Signature" className="w-full h-16 object-contain bg-muted rounded border border-input" /></div>
                  )}
                  {pod.photo_urls?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground mb-1">Photos ({pod.photo_urls.length}):</p>
                      <div className="grid grid-cols-3 gap-1">
                        {pod.photo_urls.map((url, i) => (
                          <img key={i} src={url} alt={`POD ${i + 1}`} className="w-full h-16 object-cover rounded border border-input" />
                        ))}
                      </div>
                    </div>
                  )}
                  {pod.notes && <p className="text-muted-foreground italic">{pod.notes}</p>}
                  <p className="text-[10px] text-muted-foreground">Delivered: {new Date(pod.delivered_at).toLocaleString()}</p>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">{load.status === 'completed' || load.status === 'invoiced' ? 'No POD submitted' : 'Pending delivery'}</p>
              )}
            </CardContent>
          </Card>

          {/* Files */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3 text-cyan-400" /> FILES</CardTitle></CardHeader>
            <CardContent className="text-xs">
              {files.length > 0 ? files.map(f => (
                <div key={f.file_id} className="flex justify-between items-center py-1 border-b border-border last:border-0">
                  <a href={f.file_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline truncate">{f.file_name}</a>
                  <span className="text-muted-foreground text-[10px]">{new Date(f.created_at).toLocaleDateString()}</span>
                </div>
              )) : <p className="text-muted-foreground text-center py-4">No files attached</p>}
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Charges Editor Dialog */}
      <LoadChargesEditor open={showCharges} onOpenChange={(v) => { setShowCharges(v); if (!v) fetchData(); }} loadId={loadId} load={load} />
    </div>
  );
}
